import type { NamespaceCatalog } from '@/i18n/ns';

// Conversation controls follow the model-declared response language.
const en = {
  title: 'Questions',
  count: '{current} of {total}',
  putAway: 'Put these questions away',
  announceOne: '1 question — press Tab to answer.',
  announceMany: '{count} questions — press Tab to answer.',
  hint: 'Select all that apply',
  other: 'Something else…',
  typeAnswer: 'Type your answer',
  back: 'Back',
  skip: 'Skip',
  submit: 'Submit',
  next: 'Next',
  askedOne: 'Asked 1 question',
  askedMany: 'Asked {count} questions',
  answer: 'Answer',
  noPreference: 'no preference',
  skipAnswer: 'Skip these questions. Continue with your best assumption and say what you assumed.',
  compare: 'View larger',
  useThis: 'Use this',
  mixPlaceholder: 'e.g. A with the colours of C',
  mixSend: 'Send',
  moreDirections: 'Show other directions',
  moreDirectionsMessage: 'Show me 2–3 other directions.',
  choices: 'Choices',
  choicesClosed: 'These choices are no longer open.',
};

type QuestionCopyKey = keyof typeof en;
const questionsCopy: NamespaceCatalog<QuestionCopyKey> = {
  en,
  sv: {
    title: 'Frågor',
    count: '{current} av {total}',
    putAway: 'Lägg undan frågorna',
    announceOne: '1 fråga — tryck på Tab för att svara.',
    announceMany: '{count} frågor — tryck på Tab för att svara.',
    hint: 'Välj alla som stämmer',
    other: 'Något annat…',
    typeAnswer: 'Skriv ditt svar',
    back: 'Tillbaka',
    skip: 'Hoppa över',
    submit: 'Skicka',
    next: 'Nästa',
    askedOne: 'Ställde 1 fråga',
    askedMany: 'Ställde {count} frågor',
    answer: 'Svara',
    noPreference: 'ingen preferens',
    skipAnswer: 'Hoppa över frågorna. Fortsätt med ditt bästa antagande och säg vad du antog.',
    compare: 'Visa större',
    useThis: 'Använd den här',
    mixPlaceholder: 't.ex. A med färgerna från C',
    mixSend: 'Skicka',
    moreDirections: 'Visa andra riktningar',
    moreDirectionsMessage: 'Visa mig 2–3 andra riktningar.',
    choices: 'Val',
    choicesClosed: 'De här valen är inte längre öppna.',
  },
  da: {
    title: 'Spørgsmål',
    count: '{current} af {total}',
    putAway: 'Læg spørgsmålene væk',
    announceOne: '1 spørgsmål — tryk Tab for at svare.',
    announceMany: '{count} spørgsmål — tryk Tab for at svare.',
    hint: 'Vælg alle relevante svar',
    other: 'Noget andet…',
    typeAnswer: 'Skriv dit svar',
    back: 'Tilbage',
    skip: 'Spring over',
    submit: 'Send',
    next: 'Næste',
    askedOne: 'Stillede 1 spørgsmål',
    askedMany: 'Stillede {count} spørgsmål',
    answer: 'Svar',
    noPreference: 'ingen præference',
    skipAnswer:
      'Spring spørgsmålene over. Fortsæt med din bedste antagelse og fortæl, hvad du antog.',
    compare: 'Vis større',
    useThis: 'Brug denne',
    mixPlaceholder: 'fx A med farverne fra C',
    mixSend: 'Send',
    moreDirections: 'Vis andre retninger',
    moreDirectionsMessage: 'Vis mig 2–3 andre retninger.',
    choices: 'Valg',
    choicesClosed: 'Disse valg er ikke længere åbne.',
  },
  de: {
    title: 'Fragen',
    count: '{current} von {total}',
    putAway: 'Fragen ausblenden',
    announceOne: '1 Frage — zum Antworten Tab drücken.',
    announceMany: '{count} Fragen — zum Antworten Tab drücken.',
    hint: 'Alles Zutreffende auswählen',
    other: 'Etwas anderes…',
    typeAnswer: 'Antwort eingeben',
    back: 'Zurück',
    skip: 'Überspringen',
    submit: 'Senden',
    next: 'Weiter',
    askedOne: '1 Frage gestellt',
    askedMany: '{count} Fragen gestellt',
    answer: 'Antworten',
    noPreference: 'keine Präferenz',
    skipAnswer:
      'Überspringen Sie diese Fragen. Fahren Sie mit Ihrer besten Annahme fort und nennen Sie, was Sie angenommen haben.',
    compare: 'Größer anzeigen',
    useThis: 'Diese verwenden',
    mixPlaceholder: 'z. B. A mit den Farben von C',
    mixSend: 'Senden',
    moreDirections: 'Andere Richtungen zeigen',
    moreDirectionsMessage: 'Zeig mir 2–3 andere Richtungen.',
    choices: 'Auswahl',
    choicesClosed: 'Diese Auswahl ist nicht mehr offen.',
  },
  fr: {
    title: 'Questions',
    count: '{current} sur {total}',
    putAway: 'Masquer ces questions',
    announceOne: '1 question — appuyez sur Tab pour répondre.',
    announceMany: '{count} questions — appuyez sur Tab pour répondre.',
    hint: 'Sélectionnez toutes les réponses pertinentes',
    other: 'Autre chose…',
    typeAnswer: 'Saisissez votre réponse',
    back: 'Retour',
    skip: 'Passer',
    submit: 'Envoyer',
    next: 'Suivant',
    askedOne: '1 question posée',
    askedMany: '{count} questions posées',
    answer: 'Répondre',
    noPreference: 'aucune préférence',
    skipAnswer: 'Passe ces questions. Continue avec ton hypothèse la plus probable et indique-la.',
    compare: 'Agrandir',
    useThis: 'Utiliser celle-ci',
    mixPlaceholder: 'p. ex. A avec les couleurs de C',
    mixSend: 'Envoyer',
    moreDirections: 'Voir d’autres directions',
    moreDirectionsMessage: 'Montre-moi 2 ou 3 autres directions.',
    choices: 'Choix',
    choicesClosed: 'Ces choix ne sont plus ouverts.',
  },
  es: {
    title: 'Preguntas',
    count: '{current} de {total}',
    putAway: 'Ocultar estas preguntas',
    announceOne: '1 pregunta — pulsa Tab para responder.',
    announceMany: '{count} preguntas — pulsa Tab para responder.',
    hint: 'Selecciona todas las opciones que correspondan',
    other: 'Otra cosa…',
    typeAnswer: 'Escribe tu respuesta',
    back: 'Atrás',
    skip: 'Omitir',
    submit: 'Enviar',
    next: 'Siguiente',
    askedOne: '1 pregunta formulada',
    askedMany: '{count} preguntas formuladas',
    answer: 'Responder',
    noPreference: 'sin preferencia',
    skipAnswer: 'Omite estas preguntas. Continúa con tu mejor suposición e indica cuál es.',
    compare: 'Ver más grande',
    useThis: 'Usar esta',
    mixPlaceholder: 'p. ej., A con los colores de C',
    mixSend: 'Enviar',
    moreDirections: 'Ver otras direcciones',
    moreDirectionsMessage: 'Muéstrame 2–3 direcciones distintas.',
    choices: 'Opciones',
    choicesClosed: 'Estas opciones ya no están abiertas.',
  },
  it: {
    title: 'Domande',
    count: '{current} di {total}',
    putAway: 'Nascondi queste domande',
    announceOne: '1 domanda — premi Tab per rispondere.',
    announceMany: '{count} domande — premi Tab per rispondere.',
    hint: 'Seleziona tutte le opzioni pertinenti',
    other: 'Altro…',
    typeAnswer: 'Scrivi la tua risposta',
    back: 'Indietro',
    skip: 'Salta',
    submit: 'Invia',
    next: 'Avanti',
    askedOne: '1 domanda posta',
    askedMany: '{count} domande poste',
    answer: 'Rispondi',
    noPreference: 'nessuna preferenza',
    skipAnswer:
      'Salta queste domande. Continua con la tua ipotesi migliore e indica quale hai scelto.',
    compare: 'Visualizza più grande',
    useThis: 'Usa questa',
    mixPlaceholder: 'es. A con i colori di C',
    mixSend: 'Invia',
    moreDirections: 'Mostra altre direzioni',
    moreDirectionsMessage: 'Mostrami 2–3 altre direzioni.',
    choices: 'Scelte',
    choicesClosed: 'Queste scelte non sono più aperte.',
  },
  nl: {
    title: 'Vragen',
    count: '{current} van {total}',
    putAway: 'Deze vragen verbergen',
    announceOne: '1 vraag — druk op Tab om te antwoorden.',
    announceMany: '{count} vragen — druk op Tab om te antwoorden.',
    hint: 'Selecteer alles wat van toepassing is',
    other: 'Iets anders…',
    typeAnswer: 'Typ je antwoord',
    back: 'Terug',
    skip: 'Overslaan',
    submit: 'Versturen',
    next: 'Volgende',
    askedOne: '1 vraag gesteld',
    askedMany: '{count} vragen gesteld',
    answer: 'Antwoorden',
    noPreference: 'geen voorkeur',
    skipAnswer: 'Sla deze vragen over. Ga verder met je beste aanname en geef aan wat je aannam.',
    compare: 'Groter bekijken',
    useThis: 'Deze gebruiken',
    mixPlaceholder: 'bijv. A met de kleuren van C',
    mixSend: 'Versturen',
    moreDirections: 'Andere richtingen tonen',
    moreDirectionsMessage: 'Laat me 2–3 andere richtingen zien.',
    choices: 'Keuzes',
    choicesClosed: 'Deze keuzes zijn niet meer open.',
  },
  fi: {
    title: 'Kysymykset',
    count: '{current} / {total}',
    putAway: 'Piilota nämä kysymykset',
    announceOne: '1 kysymys — vastaa painamalla Tab.',
    announceMany: '{count} kysymystä — vastaa painamalla Tab.',
    hint: 'Valitse kaikki sopivat vaihtoehdot',
    other: 'Jotain muuta…',
    typeAnswer: 'Kirjoita vastauksesi',
    back: 'Takaisin',
    skip: 'Ohita',
    submit: 'Lähetä',
    next: 'Seuraava',
    askedOne: 'Esitettiin 1 kysymys',
    askedMany: 'Esitettiin {count} kysymystä',
    answer: 'Vastaa',
    noPreference: 'ei mieltymystä',
    skipAnswer:
      'Ohita nämä kysymykset. Jatka parhaan oletuksesi perusteella ja kerro, mitä oletit.',
    compare: 'Näytä suurempana',
    useThis: 'Käytä tätä',
    mixPlaceholder: 'esim. A mutta C:n väreillä',
    mixSend: 'Lähetä',
    moreDirections: 'Näytä muita suuntia',
    moreDirectionsMessage: 'Näytä minulle 2–3 muuta suuntaa.',
    choices: 'Vaihtoehdot',
    choicesClosed: 'Nämä vaihtoehdot eivät ole enää avoinna.',
  },
  pt: {
    title: 'Perguntas',
    count: '{current} de {total}',
    putAway: 'Ocultar estas perguntas',
    announceOne: '1 pergunta — prima Tab para responder.',
    announceMany: '{count} perguntas — prima Tab para responder.',
    hint: 'Selecione todas as opções aplicáveis',
    other: 'Outra opção…',
    typeAnswer: 'Escreva a sua resposta',
    back: 'Voltar',
    skip: 'Ignorar',
    submit: 'Enviar',
    next: 'Seguinte',
    askedOne: '1 pergunta feita',
    askedMany: '{count} perguntas feitas',
    answer: 'Responder',
    noPreference: 'sem preferência',
    skipAnswer: 'Ignora estas perguntas. Continua com a tua melhor suposição e indica qual foi.',
    compare: 'Ver maior',
    useThis: 'Usar esta',
    mixPlaceholder: 'p. ex., A com as cores de C',
    mixSend: 'Enviar',
    moreDirections: 'Mostrar outras direções',
    moreDirectionsMessage: 'Mostra-me 2–3 outras direções.',
    choices: 'Opções',
    choicesClosed: 'Estas opções já não estão abertas.',
  },
  pl: {
    title: 'Pytania',
    count: '{current} z {total}',
    putAway: 'Ukryj te pytania',
    announceOne: '1 pytanie — naciśnij Tab, aby odpowiedzieć.',
    announceMany: '{count} pytań — naciśnij Tab, aby odpowiedzieć.',
    hint: 'Wybierz wszystkie pasujące odpowiedzi',
    other: 'Coś innego…',
    typeAnswer: 'Wpisz odpowiedź',
    back: 'Wstecz',
    skip: 'Pomiń',
    submit: 'Wyślij',
    next: 'Dalej',
    askedOne: 'Zadano 1 pytanie',
    askedMany: 'Zadano {count} pytań',
    answer: 'Odpowiedz',
    noPreference: 'bez preferencji',
    skipAnswer: 'Pomiń te pytania. Kontynuuj na podstawie najlepszego założenia i podaj je.',
    compare: 'Pokaż większe',
    useThis: 'Użyj tego',
    mixPlaceholder: 'np. A z kolorami z C',
    mixSend: 'Wyślij',
    moreDirections: 'Pokaż inne kierunki',
    moreDirectionsMessage: 'Pokaż mi 2–3 inne kierunki.',
    choices: 'Opcje',
    choicesClosed: 'Te opcje nie są już dostępne.',
  },
  cs: {
    title: 'Otázky',
    count: '{current} z {total}',
    putAway: 'Skrýt tyto otázky',
    announceOne: '1 otázka — stiskněte Tab pro odpověď.',
    announceMany: '{count} otázek — stiskněte Tab pro odpověď.',
    hint: 'Vyberte všechny vhodné možnosti',
    other: 'Něco jiného…',
    typeAnswer: 'Napište odpověď',
    back: 'Zpět',
    skip: 'Přeskočit',
    submit: 'Odeslat',
    next: 'Další',
    askedOne: 'Položena 1 otázka',
    askedMany: 'Položeno {count} otázek',
    answer: 'Odpovědět',
    noPreference: 'bez preference',
    skipAnswer: 'Přeskoč tyto otázky. Pokračuj s nejlepším předpokladem a uveď ho.',
    compare: 'Zobrazit větší',
    useThis: 'Použít tento',
    mixPlaceholder: 'např. A s barvami z C',
    mixSend: 'Odeslat',
    moreDirections: 'Zobrazit další směry',
    moreDirectionsMessage: 'Ukaž mi 2–3 další směry.',
    choices: 'Možnosti',
    choicesClosed: 'Tyto možnosti už nejsou otevřené.',
  },
  sk: {
    title: 'Otázky',
    count: '{current} z {total}',
    putAway: 'Skryť tieto otázky',
    announceOne: '1 otázka — stlačte Tab na odpoveď.',
    announceMany: '{count} otázok — stlačte Tab na odpoveď.',
    hint: 'Vyberte všetky vhodné možnosti',
    other: 'Niečo iné…',
    typeAnswer: 'Napíšte odpoveď',
    back: 'Späť',
    skip: 'Preskočiť',
    submit: 'Odoslať',
    next: 'Ďalej',
    askedOne: 'Položená 1 otázka',
    askedMany: 'Položených {count} otázok',
    answer: 'Odpovedať',
    noPreference: 'bez preferencie',
    skipAnswer: 'Preskoč tieto otázky. Pokračuj s najlepším predpokladom a uveď ho.',
    compare: 'Zobraziť väčšie',
    useThis: 'Použiť tento',
    mixPlaceholder: 'napr. A s farbami z C',
    mixSend: 'Odoslať',
    moreDirections: 'Zobraziť ďalšie smery',
    moreDirectionsMessage: 'Ukáž mi 2–3 ďalšie smery.',
    choices: 'Možnosti',
    choicesClosed: 'Tieto možnosti už nie sú otvorené.',
  },
  sl: {
    title: 'Vprašanja',
    count: '{current} od {total}',
    putAway: 'Skrij ta vprašanja',
    announceOne: '1 vprašanje — pritisnite Tab za odgovor.',
    announceMany: '{count} vprašanj — pritisnite Tab za odgovor.',
    hint: 'Izberite vse ustrezne možnosti',
    other: 'Nekaj drugega…',
    typeAnswer: 'Vnesite odgovor',
    back: 'Nazaj',
    skip: 'Preskoči',
    submit: 'Pošlji',
    next: 'Naprej',
    askedOne: 'Zastavljeno 1 vprašanje',
    askedMany: 'Zastavljenih {count} vprašanj',
    answer: 'Odgovori',
    noPreference: 'brez preference',
    skipAnswer: 'Preskoči ta vprašanja. Nadaljuj z najboljšo predpostavko in jo navedi.',
    compare: 'Prikaži večje',
    useThis: 'Uporabi to',
    mixPlaceholder: 'npr. A z barvami iz C',
    mixSend: 'Pošlji',
    moreDirections: 'Prikaži druge smeri',
    moreDirectionsMessage: 'Pokaži mi 2–3 druge smeri.',
    choices: 'Možnosti',
    choicesClosed: 'Te možnosti niso več odprte.',
  },
  hr: {
    title: 'Pitanja',
    count: '{current} od {total}',
    putAway: 'Sakrij ova pitanja',
    announceOne: '1 pitanje — pritisnite Tab za odgovor.',
    announceMany: '{count} pitanja — pritisnite Tab za odgovor.',
    hint: 'Odaberite sve primjenjive opcije',
    other: 'Nešto drugo…',
    typeAnswer: 'Upišite odgovor',
    back: 'Natrag',
    skip: 'Preskoči',
    submit: 'Pošalji',
    next: 'Dalje',
    askedOne: 'Postavljeno 1 pitanje',
    askedMany: 'Postavljeno {count} pitanja',
    answer: 'Odgovori',
    noPreference: 'bez preferencije',
    skipAnswer: 'Preskoči ova pitanja. Nastavi s najboljom pretpostavkom i navedi je.',
    compare: 'Prikaži veće',
    useThis: 'Koristi ovo',
    mixPlaceholder: 'npr. A s bojama iz C',
    mixSend: 'Pošalji',
    moreDirections: 'Prikaži druge smjerove',
    moreDirectionsMessage: 'Pokaži mi 2–3 druga smjera.',
    choices: 'Mogućnosti',
    choicesClosed: 'Ove mogućnosti više nisu otvorene.',
  },
  bg: {
    title: 'Въпроси',
    count: '{current} от {total}',
    putAway: 'Скрий тези въпроси',
    announceOne: '1 въпрос — натиснете Tab, за да отговорите.',
    announceMany: '{count} въпроса — натиснете Tab, за да отговорите.',
    hint: 'Изберете всички подходящи отговори',
    other: 'Нещо друго…',
    typeAnswer: 'Напишете отговора си',
    back: 'Назад',
    skip: 'Пропусни',
    submit: 'Изпрати',
    next: 'Напред',
    askedOne: 'Зададен е 1 въпрос',
    askedMany: 'Зададени са {count} въпроса',
    answer: 'Отговори',
    noPreference: 'нямам предпочитание',
    skipAnswer: 'Пропусни тези въпроси. Продължи с най-доброто си предположение и го посочи.',
    compare: 'Покажи по-голямо',
    useThis: 'Използване на това',
    mixPlaceholder: 'напр. A с цветовете на C',
    mixSend: 'Изпрати',
    moreDirections: 'Показване на други насоки',
    moreDirectionsMessage: 'Покажи ми 2–3 други насоки.',
    choices: 'Варианти',
    choicesClosed: 'Тези варианти вече не са отворени.',
  },
  el: {
    title: 'Ερωτήσεις',
    count: '{current} από {total}',
    putAway: 'Απόκρυψη ερωτήσεων',
    announceOne: '1 ερώτηση — πατήστε Tab για απάντηση.',
    announceMany: '{count} ερωτήσεις — πατήστε Tab για απάντηση.',
    hint: 'Επιλέξτε όλα όσα ισχύουν',
    other: 'Κάτι άλλο…',
    typeAnswer: 'Γράψτε την απάντησή σας',
    back: 'Πίσω',
    skip: 'Παράλειψη',
    submit: 'Υποβολή',
    next: 'Επόμενο',
    askedOne: 'Έγινε 1 ερώτηση',
    askedMany: 'Έγιναν {count} ερωτήσεις',
    answer: 'Απάντηση',
    noPreference: 'καμία προτίμηση',
    skipAnswer:
      'Παράλειψε αυτές τις ερωτήσεις. Συνέχισε με την καλύτερη υπόθεσή σου και ανάφερέ την.',
    compare: 'Μεγαλύτερη προβολή',
    useThis: 'Χρήση αυτής',
    mixPlaceholder: 'π.χ. A με τα χρώματα του C',
    mixSend: 'Αποστολή',
    moreDirections: 'Εμφάνιση άλλων κατευθύνσεων',
    moreDirectionsMessage: 'Δείξε μου 2–3 άλλες κατευθύνσεις.',
    choices: 'Επιλογές',
    choicesClosed: 'Αυτές οι επιλογές δεν είναι πλέον ανοιχτές.',
  },
  hu: {
    title: 'Kérdések',
    count: '{current} / {total}',
    putAway: 'Kérdések elrejtése',
    announceOne: '1 kérdés — válaszhoz nyomja meg a Tab billentyűt.',
    announceMany: '{count} kérdés — válaszhoz nyomja meg a Tab billentyűt.',
    hint: 'Válassza ki az összes megfelelőt',
    other: 'Valami más…',
    typeAnswer: 'Írja be a válaszát',
    back: 'Vissza',
    skip: 'Kihagyás',
    submit: 'Küldés',
    next: 'Tovább',
    askedOne: '1 kérdés feltéve',
    askedMany: '{count} kérdés feltéve',
    answer: 'Válasz',
    noPreference: 'nincs preferencia',
    skipAnswer:
      'Hagyd ki ezeket a kérdéseket. Folytasd a legjobb feltételezésed alapján, és nevezd meg azt.',
    compare: 'Nagyobb nézet',
    useThis: 'Ezt használom',
    mixPlaceholder: 'pl. A a C színeivel',
    mixSend: 'Küldés',
    moreDirections: 'További irányok',
    moreDirectionsMessage: 'Mutass 2–3 másik irányt.',
    choices: 'Lehetőségek',
    choicesClosed: 'Ezek a lehetőségek már nem nyitottak.',
  },
  ro: {
    title: 'Întrebări',
    count: '{current} din {total}',
    putAway: 'Ascunde aceste întrebări',
    announceOne: '1 întrebare — apăsați Tab pentru a răspunde.',
    announceMany: '{count} întrebări — apăsați Tab pentru a răspunde.',
    hint: 'Selectați toate opțiunile potrivite',
    other: 'Altceva…',
    typeAnswer: 'Scrieți răspunsul',
    back: 'Înapoi',
    skip: 'Omite',
    submit: 'Trimite',
    next: 'Înainte',
    askedOne: '1 întrebare adresată',
    askedMany: '{count} întrebări adresate',
    answer: 'Răspunde',
    noPreference: 'fără preferință',
    skipAnswer:
      'Omite aceste întrebări. Continuă cu cea mai bună presupunere și precizează ce ai presupus.',
    compare: 'Vezi mai mare',
    useThis: 'Folosește aceasta',
    mixPlaceholder: 'de ex. A cu culorile din C',
    mixSend: 'Trimite',
    moreDirections: 'Arată alte direcții',
    moreDirectionsMessage: 'Arată-mi 2–3 alte direcții.',
    choices: 'Opțiuni',
    choicesClosed: 'Aceste opțiuni nu mai sunt deschise.',
  },
  et: {
    title: 'Küsimused',
    count: '{current} / {total}',
    putAway: 'Peida need küsimused',
    announceOne: '1 küsimus — vastamiseks vajutage Tab.',
    announceMany: '{count} küsimust — vastamiseks vajutage Tab.',
    hint: 'Valige kõik sobivad vastused',
    other: 'Midagi muud…',
    typeAnswer: 'Kirjutage vastus',
    back: 'Tagasi',
    skip: 'Jäta vahele',
    submit: 'Saada',
    next: 'Edasi',
    askedOne: 'Esitati 1 küsimus',
    askedMany: 'Esitati {count} küsimust',
    answer: 'Vasta',
    noPreference: 'eelistus puudub',
    skipAnswer: 'Jäta need küsimused vahele. Jätka parima oletusega ja ütle, mida eeldasid.',
    compare: 'Näita suuremalt',
    useThis: 'Kasuta seda',
    mixPlaceholder: 'nt A, aga C värvidega',
    mixSend: 'Saada',
    moreDirections: 'Näita teisi suundi',
    moreDirectionsMessage: 'Näita mulle 2–3 teist suunda.',
    choices: 'Valikud',
    choicesClosed: 'Need valikud ei ole enam avatud.',
  },
  lv: {
    title: 'Jautājumi',
    count: '{current} no {total}',
    putAway: 'Paslēpt šos jautājumus',
    announceOne: '1 jautājums — nospiediet Tab, lai atbildētu.',
    announceMany: '{count} jautājumi — nospiediet Tab, lai atbildētu.',
    hint: 'Izvēlieties visas atbilstošās iespējas',
    other: 'Kaut kas cits…',
    typeAnswer: 'Ierakstiet atbildi',
    back: 'Atpakaļ',
    skip: 'Izlaist',
    submit: 'Nosūtīt',
    next: 'Tālāk',
    askedOne: 'Uzdots 1 jautājums',
    askedMany: 'Uzdoti {count} jautājumi',
    answer: 'Atbildēt',
    noPreference: 'nav priekšrocības',
    skipAnswer: 'Izlaid šos jautājumus. Turpini ar labāko pieņēmumu un norādi, ko pieņēmi.',
    compare: 'Skatīt lielāku',
    useThis: 'Izmantot šo',
    mixPlaceholder: 'piem., A ar C krāsām',
    mixSend: 'Nosūtīt',
    moreDirections: 'Rādīt citus virzienus',
    moreDirectionsMessage: 'Parādi man 2–3 citus virzienus.',
    choices: 'Izvēles',
    choicesClosed: 'Šīs izvēles vairs nav atvērtas.',
  },
  lt: {
    title: 'Klausimai',
    count: '{current} iš {total}',
    putAway: 'Slėpti šiuos klausimus',
    announceOne: '1 klausimas — paspauskite Tab, kad atsakytumėte.',
    announceMany: '{count} klausimai — paspauskite Tab, kad atsakytumėte.',
    hint: 'Pasirinkite visus tinkamus variantus',
    other: 'Kas nors kita…',
    typeAnswer: 'Įrašykite atsakymą',
    back: 'Atgal',
    skip: 'Praleisti',
    submit: 'Siųsti',
    next: 'Toliau',
    askedOne: 'Užduotas 1 klausimas',
    askedMany: 'Užduoti {count} klausimai',
    answer: 'Atsakyti',
    noPreference: 'neturiu pageidavimo',
    skipAnswer: 'Praleisk šiuos klausimus. Tęsk remdamasis geriausia prielaida ir ją nurodyk.',
    compare: 'Rodyti didesnį',
    useThis: 'Naudoti šią',
    mixPlaceholder: 'pvz., A su C spalvomis',
    mixSend: 'Siųsti',
    moreDirections: 'Rodyti kitas kryptis',
    moreDirectionsMessage: 'Parodyk man 2–3 kitas kryptis.',
    choices: 'Pasirinkimai',
    choicesClosed: 'Šie pasirinkimai nebėra atviri.',
  },
  ga: {
    title: 'Ceisteanna',
    count: '{current} as {total}',
    putAway: 'Cuir na ceisteanna seo i bhfolach',
    announceOne: '1 cheist — brúigh Tab chun freagra a thabhairt.',
    announceMany: '{count} ceist — brúigh Tab chun freagra a thabhairt.',
    hint: 'Roghnaigh gach ceann a bhaineann leat',
    other: 'Rud éigin eile…',
    typeAnswer: 'Clóscríobh do fhreagra',
    back: 'Ar ais',
    skip: 'Scipeáil',
    submit: 'Seol',
    next: 'Ar aghaidh',
    askedOne: 'Cuireadh 1 cheist',
    askedMany: 'Cuireadh {count} ceist',
    answer: 'Freagair',
    noPreference: 'gan rogha ar leith',
    skipAnswer:
      'Scipeáil na ceisteanna seo. Lean ar aghaidh leis an toimhde is fearr atá agat agus luaigh í.',
    compare: 'Féach níos mó',
    useThis: 'Úsáid é seo',
    mixPlaceholder: 'm.sh. A le dathanna C',
    mixSend: 'Seol',
    moreDirections: 'Taispeáin treoracha eile',
    moreDirectionsMessage: 'Taispeáin 2–3 threo eile dom.',
    choices: 'Roghanna',
    choicesClosed: 'Níl na roghanna seo oscailte a thuilleadh.',
  },
  mt: {
    title: 'Mistoqsijiet',
    count: '{current} minn {total}',
    putAway: 'Aħbi dawn il-mistoqsijiet',
    announceOne: 'Mistoqsija waħda — agħfas Tab biex twieġeb.',
    announceMany: '{count} mistoqsijiet — agħfas Tab biex twieġeb.',
    hint: 'Agħżel dak kollu li japplika',
    other: 'Xi ħaġa oħra…',
    typeAnswer: 'Ikteb it-tweġiba tiegħek',
    back: 'Lura',
    skip: 'Aqbeż',
    submit: 'Ibgħat',
    next: 'Li jmiss',
    askedOne: 'Saret mistoqsija waħda',
    askedMany: 'Saru {count} mistoqsijiet',
    answer: 'Wieġeb',
    noPreference: 'ebda preferenza',
    skipAnswer:
      'Aqbeż dawn il-mistoqsijiet. Kompli bl-aħjar suppożizzjoni tiegħek u għid x’assumejt.',
    compare: 'Ara akbar',
    useThis: 'Uża dan',
    mixPlaceholder: 'eż. A bil-kuluri ta’ C',
    mixSend: 'Ibgħat',
    moreDirections: 'Uri direzzjonijiet oħra',
    moreDirectionsMessage: 'Urini 2–3 direzzjonijiet oħra.',
    choices: 'Għażliet',
    choicesClosed: "Dawn l-għażliet m'għadhomx miftuħa.",
  },
};
export default questionsCopy;
